import {
  ProjectFileType,
  useGetProjectVersionQuery,
} from '@/generated/graphql';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { Typography } from '@mui/material';
import FileTreeView from '../components/FileTreeView';
import { LoadingSpinner } from '@/common/components/loading/LoadingSpinner';
import { useProjectDetails } from '../context/ProjectDetailsContext';

interface FileWithVersion extends ProjectFileType {
  version: number;
}

export interface FilesTabRef {
  refreshData: () => Promise<void>;
}

const FileExplorerTab = forwardRef<FilesTabRef>((_props, ref) => {
  const { project, projectDirPath, selectedVersion } = useProjectDetails();

  // Single query for any version
  const {
    data: versionData,
    loading: versionLoading,
    refetch: refetchVersion,
  } = useGetProjectVersionQuery({
    variables: {
      projectId: project?._id || '',
      version: selectedVersion,
    },
    skip: !project?._id,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true, // Show loading state when refetching
  });

  const displayedFiles = useMemo(
    () => versionData?.projectVersion?.files || [],
    [versionData?.projectVersion?.files]
  );

  const isLoading = versionLoading;

  // Expose refresh function through ref - memoize the callback
  useImperativeHandle(
    ref,
    () => ({
      refreshData: async () => {
        await refetchVersion();
      },
    }),
    [refetchVersion]
  );

  // Convert files to include version number for display
  const filesWithVersion: FileWithVersion[] = useMemo(() => {
    return displayedFiles.map((file) => ({
      ...file,
      version: selectedVersion,
    }));
  }, [displayedFiles, selectedVersion]);

  return (
    <>
      {isLoading ? (
        <LoadingSpinner
          message={`Loading ${
            selectedVersion === project?.versionHistory?.length
              ? 'latest'
              : `version ${selectedVersion}`
          }...`}
        />
      ) : displayedFiles.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          py={4}
        >
          {!project?.versionHistory?.length
            ? 'No files in this project yet.'
            : 'No files in this version.'}
        </Typography>
      ) : (
        <FileTreeView
          files={filesWithVersion}
          projectDirPath={projectDirPath}
          projectId={project?._id || ''}
        />
      )}
    </>
  );
});

FileExplorerTab.displayName = 'FileExplorerTab';

export default FileExplorerTab;
